import { useMemo, useState } from 'react';

import { NewPriceVersionDialog } from './new-price-version-dialog';
import { PriceHistoryDialog } from './price-history-sheet';
import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import type { LlmPriceVersionDto } from '../../../../../../backend/src/llm/pricing/dto/llm-pricing.response.dto';
import { useFetchLlmPrices } from '@/api/queries/analyticsQueries/useFetchLlmPrices';
import { usePlatformPermissionCheck } from '@/hooks/usePlatformPermissionCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { SearchInput } from '@/new/components/search-input';
import {
  SettingsContent,
  SettingsRow,
  SettingsSection,
  SettingsSectionHeading,
} from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

/** Trims the trailing zeroes a numeric(12,6) round-trip leaves behind. */
const formatRate = (value: number) => `$${Number(value.toFixed(6))}`;

/** Strips the shared `anthropic.` prefix so the model column stays scannable. */
const shortModelName = (model: string) => model.replace(/^anthropic\./, '');

const RATE_COLUMNS = [
  { key: 'inputUsdPerMTok', label: 'Input' },
  { key: 'cacheReadUsdPerMTok', label: 'Cache read' },
  { key: 'cacheWriteUsdPerMTok', label: 'Cache write' },
  { key: 'outputUsdPerMTok', label: 'Output' },
] as const;

export function LlmPricing() {
  usePlatformPermissionCheck(Permission.PlatformManageLlmPricing);

  const { data: pricesData, isLoading: isPricesLoading, isError } = useFetchLlmPrices();

  const [historyModel, setHistoryModel] = useState<string | null>(null);
  const [modelSearch, setModelSearch] = useState('');

  const geos = useMemo(
    () => [...new Set(pricesData?.current.map((price) => price.servingGeo) ?? [])].sort(),
    [pricesData],
  );

  // Grouped so the matrix reads model-per-row, geo-per-column — the shape that
  // makes an accidental divergence between geos obvious at a glance.
  const byModel = useMemo(() => {
    const grouped = new Map<string, Map<string, LlmPriceVersionDto>>();
    for (const price of pricesData?.current ?? []) {
      const forModel = grouped.get(price.model) ?? new Map<string, LlmPriceVersionDto>();
      forModel.set(price.servingGeo, price);
      grouped.set(price.model, forModel);
    }
    return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [pricesData]);

  const filteredByModel = useMemo(() => {
    const search = modelSearch.trim().toLocaleLowerCase();
    if (!search) return byModel;

    return byModel.filter(([model]) => model.toLocaleLowerCase().includes(search));
  }, [byModel, modelSearch]);

  const gaps = pricesData?.gaps ?? [];
  const knownModels = useMemo(
    () =>
      [
        ...new Set([
          ...(pricesData?.current.map(({ model }) => model) ?? []),
          ...(pricesData?.history.map(({ model }) => model) ?? []),
          ...(pricesData?.gaps.map(({ model }) => model) ?? []),
        ]),
      ].sort(),
    [pricesData],
  );

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={[
          { href: '/admin/organisations', label: 'Organisations', active: false },
          { href: '/admin/llm-pricing', label: 'LLM Pricing', active: true },
        ]}
      />
      <div className="min-h-0 flex-1 overflow-auto px-8 pt-7 pb-8">
        <SettingsContent>
          {isError && (
            <div className="rounded-xl border border-red-300 bg-red-50 p-4">
              <div className="flex items-center gap-2 font-medium text-red-900">
                <Icon className="lucide--circle-alert text-base" />
                <span>Could not load prices</span>
              </div>
              <p className="mt-1 text-red-800">
                Nothing below is trustworthy — an empty rate matrix here does not mean no prices are configured, and a
                missing warning does not mean every model is priced. Reload to try again.
              </p>
            </div>
          )}

          {gaps.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-center gap-2 font-medium text-amber-900">
                <Icon className="lucide--triangle-alert text-base" />
                <span>{gaps.length === 1 ? '1 model is' : `${gaps.length} models are`} billing uncosted</span>
              </div>
              <p className="mt-1 text-amber-800">
                Kim has served these but no price version covers them, so their spend is recorded with no cost or
                credits. Add a price version for each.
              </p>
              <ul className="mt-3 space-y-1">
                {gaps.map((gap) => (
                  <li key={`${gap.model} ${gap.servingGeo}`} className="text-amber-900">
                    <span className="font-mono">{shortModelName(gap.model)}</span> in{' '}
                    <span className="font-mono">{gap.servingGeo}</span> —{' '}
                    {gap.usageRowCount === 1 ? '1 turn' : `${gap.usageRowCount} turns`} uncosted
                  </li>
                ))}
              </ul>
            </div>
          )}

          <SettingsSection
            title="Price versions"
            description="Manage the prices Kim uses to cost each turn. Versions are append-only, keeping historical spend tied to the rate it was billed under."
          >
            <SettingsRow
              title="New price version"
              description="Add pricing for a new model or update the rates for an existing model and serving geo."
            >
              <NewPriceVersionDialog knownModels={knownModels} />
            </SettingsRow>
          </SettingsSection>

          <section className="space-y-8">
            <SettingsSectionHeading
              title="Current rates"
              description="USD per million tokens, by model and Bedrock cross-region inference geo."
            />
            {isPricesLoading ? (
              <div className="overflow-hidden rounded-xl border bg-white p-5 shadow-lg/1">
                <Skeleton className="h-64 w-full" />
              </div>
            ) : isError ? (
              <p className="rounded-xl border bg-white p-5 text-gray-500 shadow-lg/1">
                Rates unavailable — see the error above.
              </p>
            ) : byModel.length === 0 ? (
              <p className="rounded-xl border bg-white p-5 text-gray-500 shadow-lg/1">No price versions yet.</p>
            ) : (
              <div className="space-y-4">
                <SearchInput
                  className="w-full"
                  value={modelSearch}
                  onChange={(event) => setModelSearch(event.target.value)}
                  placeholder="Search models"
                  aria-label="Search models"
                />
                <div className="overflow-hidden rounded-xl border bg-white shadow-lg/1">
                  {filteredByModel.length === 0 ? (
                    <p className="px-5 py-8 text-center text-gray-500">No models match “{modelSearch.trim()}”.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table className="border-separate border-spacing-0">
                        <TableHeader className="bg-gray-50 [&_th]:h-auto [&_th]:px-3 [&_th]:py-2.25 [&_tr]:border-0">
                          <TableRow>
                            <TableHead className="whitespace-nowrap">Model</TableHead>
                            <TableHead className="border-l border-gray-200 whitespace-nowrap">Rate</TableHead>
                            {geos.map((geo) => (
                              <TableHead key={geo} className="border-l border-gray-200 font-mono whitespace-nowrap">
                                {geo}
                              </TableHead>
                            ))}
                            <TableHead className="border-l border-gray-200">
                              <span>Price history</span>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody className="[&_td]:border-t [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-2.25 [&_tr]:border-0">
                          {filteredByModel.map(([model, byGeo]) =>
                            RATE_COLUMNS.map((rate, rateIndex) => (
                              <TableRow key={`${model} ${rate.key}`}>
                                {rateIndex === 0 && (
                                  <TableCell
                                    rowSpan={RATE_COLUMNS.length}
                                    className="relative align-top font-mono whitespace-nowrap"
                                  >
                                    <span>{shortModelName(model)}</span>
                                  </TableCell>
                                )}
                                <TableCell className="border-l border-gray-200 whitespace-nowrap text-gray-500">
                                  {rate.label}
                                </TableCell>
                                {geos.map((geo) => {
                                  const price = byGeo.get(geo);
                                  return (
                                    <TableCell
                                      key={geo}
                                      className="border-l border-gray-200 whitespace-nowrap tabular-nums"
                                    >
                                      {price ? formatRate(price[rate.key]) : <span className="text-gray-400">—</span>}
                                    </TableCell>
                                  );
                                })}
                                {rateIndex === 0 && (
                                  <TableCell
                                    rowSpan={RATE_COLUMNS.length}
                                    className="border-l border-gray-200 align-top"
                                  >
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="p-0"
                                          aria-label={`View price history for ${shortModelName(model)}`}
                                          onClick={() => setHistoryModel(model)}
                                        >
                                          <Icon className="lucide--list" />
                                          View
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>View price history</TooltipContent>
                                    </Tooltip>
                                  </TableCell>
                                )}
                              </TableRow>
                            )),
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </SettingsContent>
      </div>

      <PriceHistoryDialog
        model={historyModel}
        onClose={() => setHistoryModel(null)}
        history={pricesData?.history ?? []}
      />
    </div>
  );
}
